<template>
  <div class="ai-assistant" :class="{ mobile: isMobile, peek: peekState }">
    <!-- Header -->
    <div class="ai-header">
      <img class="ai-badge" src="../assets/icons/ai.png" alt="" />
      <div class="title">{{ $t('ai.title') }}</div>
      <vs-button class="close-btn" circle transparent @click="handleClose" :aria-label="$t('ai.actions.close')">
        <i class="bx bx-x"></i>
      </vs-button>
    </div>

    <!-- Conversation -->
    <div class="ai-content" :class="{'empty': messages.length === 0}" ref="scroll">
      <div v-if="messages.length === 0" class="ai-hero">
        <h2>{{ $t('ai.heroText') }}</h2>
        <div class="inline-suggestions">
          <vs-button v-for="(s, i) in suggests" :key="i" class="chip" @click="quickAsk(s.label)" :aria-label="s.label">
            <i :class="['bx', s.icon]"></i>
            <span>{{ s.label }}</span>
          </vs-button>
        </div>
      </div>

      <div v-else class="thread">
        <div v-for="(m, i) in messages" :key="i" :class="['msg', m.role]">
          <component :is="m.component" v-bind="m.props" v-if="m.type === 'component'"></component>
          <div v-else-if="m.type === 'choices'" class="choices-container">
            <vs-button 
              v-for="choice in m.choices" 
              :key="choice.id" 
              class="choice-btn" 
              @click="selectChoice(choice)"
              :aria-label="choice.text">
              {{ choice.text }}
            </vs-button>
          </div>
          <div v-else-if="m.type === 'pois'" class="pois-container">
            <PoiMiniCTA 
              v-for="poi in m.pois" 
              :key="poi.id" 
              :poi-id="poi.id"
              :is-mobile="isMobile"
              :user="user"
            />
          </div>
          <div v-else-if="m.type === 'trip'" class="trip-container">
            <TripMiniCTA :trip-id="m.tripId" />
          </div>
          
          <div class="bubble" v-else-if="m.role != 'user'" v-html="m.html || escapeAndFormat(m.content)"></div>
          <div class="bubble" v-else v-html="m.html || escapeAndFormat(JSON.parse(m.content).text)"></div>
        </div>
        <div v-if="thinking" class="msg assistant">
          <div class="bubble typing">
            <span class="loading-text">{{ $t('ai.loading') }}</span>
            <span class="dot"></span><span class="dot"></span><span class="dot"></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Composer -->
    <div class="composer">
      <div class="composer-inner">

        <textarea ref="draft" v-model="draft" :placeholder="$t('ai.placeholder')" @input="autosizeTextarea" @keydown.enter.exact.prevent="send" rows="1"></textarea>

        <vs-button class="send" :disabled="!draft.trim()" @click="send" :aria-label="$t('ai.actions.send')">
          <i class="bx bx-send"></i>
        </vs-button>
      </div>
      <div class="disclaimer">{{ $t('ai.disclaimer') }}</div>
    </div>
  </div>
</template>

<script>

import PoiMiniCTA from './PoiMiniCTA.vue';
import { apiCall } from '../utils/ApiMiddleware';
import { GlobalEventEmitter } from '@/utils/GlobalEventEmitter';
import UpgradeNudge from './UpgradeNudge.vue';
import TripMiniCTA from './TripMiniCTA.vue';

export default {
  name: 'AIAssistant',
  components: { PoiMiniCTA, UpgradeNudge, TripMiniCTA },
  props: {
    isMobile: { type: Boolean, default: true },
    close: { type: Function, default: null },
    userCoords: { type: Array, default: null },
    user: { type: Object, default: null }
  },
  data() {
    return {
      messages: [],
      allMessages: [], // for history if needed
      draft: '',
      thinking: false,
      peekState: false
    };
  },
  methods: {
    handleClose() {
      if (this.close) this.close();
      else this.$emit('close');
    },
    quickAsk(s) {
      this.draft = s;
      this.send();
    },
    send() {
      const t = (this.draft || '').trim();
      if (!t) return;
      this.messages.push({ role: 'user', content: JSON.stringify({
        text: t,
        has_location_access: this.userCoords != null && this.userCoords.length === 2 && this.userCoords[0] !== 0 && this.userCoords[1] !== 0,
        user_datetime: new Date().toISOString()
      })});
      this.draft = '';
      this.$nextTick(() => {
        this.autosizeTextarea();
        this.scrollToBottom();
      });
      this.handleAssistantReply();
    },
    
    async handleAssistantReply() {
      this.thinking = true;
      
      try {
        const response = await this.callAssistantAPI();
        
        this.allMessages = response.filter(msg => msg != null && msg.role != 'system');
        this.thinking = false;
        
        // Handle the API response - check for assistant message with JSON content
        if (response && Array.isArray(response) && response.length > 0) {
          // Find the last assistant message in the array
          const assistantMessage = response.reverse().find(msg => 
            msg.role === 'assistant' && msg.content && msg.content.trim() !== ''
          );
          
          if (assistantMessage) {
            
            try {
              // Parse the JSON content
              const parsedContent = JSON.parse(assistantMessage.content);
              
              if (parsedContent && parsedContent.text) {
                // Add the text message, storing original JSON content for future API calls
                this.messages.push({
                  role: 'assistant',
                  content: parsedContent.text,
                  html: parsedContent.text,
                  originalContent: assistantMessage.content // Store original JSON
                });
                
                // If there are POIs, render them inline using PoiMiniCTA
                if (parsedContent.pois && parsedContent.pois.length > 0) {
                  this.messages.push({
                    role: 'assistant',
                    type: 'pois',
                    pois: parsedContent.pois
                  });
                }
                
                // If there are choices, add them as interactive buttons
                if (parsedContent.choices && parsedContent.choices.length > 0) {
                  this.messages.push({
                    role: 'assistant',
                    type: 'choices',
                    choices: parsedContent.choices
                  });
                }
                // If there's a trip reference, show a CTA
                if (parsedContent.trip_id || parsedContent.tripId) {
                  this.messages.push({
                    role: 'assistant',
                    type: 'trip',
                    tripId: parsedContent.trip_id || parsedContent.tripId
                  });
                }
              }
            } catch (e) {
              // If JSON parsing fails, treat as plain text
              this.messages.push({
                role: 'assistant',
                content: assistantMessage.content,
                html: assistantMessage.content,
                originalContent: assistantMessage.content
              });
            }
          }
        } else if (response && response.text && response.type === 'OPEN') {
          // Direct format: response object with text, pois, choices
          this.messages.push({
            role: 'assistant',
            content: response.text,
            html: response.text
          });
          
          // If there are POIs, render them inline using PoiMiniCTA
          if (response.pois && response.pois.length > 0) {
            this.messages.push({
              role: 'assistant',
              type: 'pois',
              pois: response.pois
            });
          }
          
          // If there are choices, add them as interactive buttons
          if (response.choices && response.choices.length > 0) {
            this.messages.push({
              role: 'assistant',
              type: 'choices',
              choices: response.choices
            });
          }
          // Trip reference
          if (response.trip_id || response.tripId) {
            this.messages.push({
              role: 'assistant',
              type: 'trip',
              tripId: response.trip_id || response.tripId
            });
          }
        } else if (response && response.messages && Array.isArray(response.messages)) {
          // Old format: array of OpenAI messages
          const assistantMessages = response.messages.filter(msg => 
            msg.role === 'assistant' && msg.content && msg.content.trim() !== ''
          );
          
          assistantMessages.forEach(msg => {
            this.messages.push({
              role: 'assistant',
              content: msg.content,
              html: msg.content
            });
          });
          
          // Handle POIs and choices from old format
          if (response.pois && response.pois.length > 0) {
            this.messages.push({
              role: 'assistant',
              type: 'pois',
              pois: response.pois
            });
          }
          
          if (response.choices && response.choices.length > 0) {
            this.messages.push({
              role: 'assistant',
              type: 'choices',
              choices: response.choices
            });
          }
          if (response.trip_id || response.tripId) {
            this.messages.push({
              role: 'assistant',
              type: 'trip',
              tripId: response.trip_id || response.tripId
            });
          }
        } else if (response && response.text) {
          // Fallback for simple text response
          this.messages.push({ 
            role: 'assistant', 
            content: response.text,
            html: response.text 
          });
        } else {
          // Fallback message if no valid response
          this.messages.push({ 
            role: 'assistant', 
            content: this.$t('ai.errors.cannotProcess'),
            html: this.$t('ai.errors.cannotProcess')
          });
        }
        
        this.$nextTick(this.scrollToBottom);
      } catch (error) {
        this.thinking = false;
        console.error('Assistant API error:', error);

        // Special handling: payment required -> show in-thread upgrade nudge
        if (error && (error.status === 402 || error.paymentRequired)) {
          this.messages.push({
            role: 'assistant',
            type: 'component',
            component: 'UpgradeNudge',
            props: {
              title: this.$t('ai.upgrade.title'),
              description: this.$t('ai.upgrade.description'),
              illustration: '/email/welcome.png',
              ctaLabel: this.$t('ai.upgrade.cta')
            }
          });
          this.$nextTick(this.scrollToBottom);
          return;
        }
        
        // Generic error message
        this.messages.push({ 
          role: 'assistant', 
          content: this.$t('ai.errors.generic'),
          html: this.$t('ai.errors.generic')
        });
        this.$nextTick(this.scrollToBottom);
      }
    },
    scrollToBottom() {
      try {
        const el = this.$refs.scroll;
        if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      } catch (e) {
        console.warn('Scroll to bottom failed', e);
      }
    },
    autosizeTextarea() {
      try {
        const el = this.$refs.draft;
        if (!el) return;
        // If empty, force exactly 1 row height to avoid UA defaults (~2 rows)
        if (!el.value || el.value.length === 0) {
          el.style.height = '22px';
          return;
        }
        // Otherwise, grow to fit up to 3 rows
        el.style.height = 'auto';
        const maxPx = 64; // ~3 rows (line-height 20px, tiny buffer)
        const newH = Math.min(el.scrollHeight, maxPx);
        el.style.height = newH + 'px';
      } catch (e) {
        // no-op
      }
    },
    escape(text) {
      const div = document.createElement('div');
      div.innerText = text;
      return div.innerHTML;
    },
    escapeAndFormat(text) {
      return this.escape(text).replace(/\n/g, '<br/>');
    },
    selectChoice(choice) {
      // Add the selected choice as a user message
      this.messages.push({ 
        role: 'user', 
        content: JSON.stringify(choice), // full obj
      });
      this.$nextTick(this.scrollToBottom);
      
      // Send the choice to the assistant
      this.handleAssistantReply();
    },
    async callAssistantAPI() {
      try {
        // Prepare the messages array following OpenAI standard format
        const messages = this.allMessages.concat(this.messages.slice(-1));

        const payload = {
          actual_latitude: this.userCoords != null ? this.userCoords[0] : 0,
          actual_longitude: this.userCoords != null ? this.userCoords[1] : 0,
          messages: messages
        };

        const response = await apiCall('POST', '/assist', payload, true);
        
        if (response.status === 200) {
          return response.data;
        } else if (response.status === 402) {
          const err = new Error('PAYMENT_REQUIRED');
          err.status = 402;
          err.paymentRequired = true;
          err.data = response.data;
          throw err;
        } else {
          const e = new Error(`API call failed with status: ${response.status}`);
          e.status = response.status;
          throw e;
        }
      } catch (error) {
        console.error('Error calling assistant API:', error);
        throw error;
      }
    }
  },
  mounted() {
    this.$nextTick(this.autosizeTextarea);
  },
  created() {
    try {
      GlobalEventEmitter.$on('aiPanelPeek', () => { this.peekState = true; });
      GlobalEventEmitter.$on('aiPanelRestore', () => { this.peekState = false; });
    } catch (e) {console.warn(e);}
  },
  beforeDestroy() {
    try {
      GlobalEventEmitter.$off('aiPanelPeek');
      GlobalEventEmitter.$off('aiPanelRestore');
    } catch (e) {console.warn(e);}
  },
  computed: {
    suggests() {
      return [
        { label: this.$t('ai.suggests.nearMe'), icon: 'bx-current-location' },
        { label: this.$t('ai.suggests.twoDayItinerary'), icon: 'bx-calendar' },
        { label: this.$t('ai.suggests.whereToEat'), icon: 'bx-food-menu' },
        { label: this.$t('ai.suggests.whatToVisitHere'), icon: 'bx-map-alt' },
      ];
    }
  }
};
</script>

<style scoped>
/* Layout */
.ai-assistant {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  color: #27303f;
  overflow-x: hidden;
  padding-top: 16px;
  /* Include padding-top in the 100% height, otherwise this container is
     16px taller than the scrollable panel and the whole sheet (header,
     close button, chips, composer) scrolls instead of just .ai-content. */
  box-sizing: border-box;
  min-height: 0;
}

/* Glass header */
.ai-header {
  position: sticky;
  top: 0;
  padding: 12px 16px 8px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  -webkit-backdrop-filter: saturate(150%) blur(12px);
  backdrop-filter: saturate(150%) blur(12px);
  border-bottom: 1px solid rgba(39, 48, 63, 0.08);
  z-index: 5;
  box-sizing: border-box;
  padding-bottom: 16px;
}
.ai-header .grabber {
  position: absolute;
  top: 6px;
  left: 50%;
  transform: translateX(-50%);
  width: 44px;
  height: 5px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.12);
}
.ai-header .title {
  font-weight: 800;
  letter-spacing: 0.2px;
}
.ai-header .close-btn {
  position: absolute;
  right: 8px;
}
.ai-header .ai-badge {
  position: absolute;
  left: 10px;
  top: 8px;
  height: 22px;
  width: auto;
  animation: aiBadgeFloat 3.6s ease-in-out infinite, aiBadgeGlow 1.8s ease-in-out infinite;
  filter: drop-shadow(0 0 10px rgba(239,126,24,0.4));
}
@keyframes aiBadgeFloat {
  0%, 100% { transform: translateY(0) scale(1); }
  50%      { transform: translateY(-3px) scale(1.04); }
}
@keyframes aiBadgeGlow {
  0%, 100% { filter: drop-shadow(0 0 8px rgba(239,126,24,0.35)); }
  50%      { filter: drop-shadow(0 0 18px rgba(239,126,24,0.75)); }
}

/* Content */
.ai-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px 16px 12px 16px;
  /* Allow this flex child to shrink below its content size so it scrolls
     internally instead of pushing the whole panel taller. */
  min-height: 0;
}
.ai-content.empty {
  display: flex;
  flex-direction: column;
  justify-content: flex-end; /* push hero to bottom above composer */
}

/* Empty hero */
.ai-hero {
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 18px;
}
.ai-hero h2 {
  margin: 0;
  font-weight: 800;
}
.inline-suggestions {
  width: 100%;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 4px 2px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  flex-wrap: nowrap;
  white-space: nowrap;
}
.inline-suggestions::-webkit-scrollbar { display: none; } /* WebKit */
.chip {
  border-radius: 999px !important;
  background: rgba(255, 255, 255, 0.5) !important;
  -webkit-backdrop-filter: saturate(150%) blur(10px);
  backdrop-filter: saturate(150%) blur(10px);
  border: 1px solid rgba(39, 48, 63, 0.12) !important;
  color: #27303f !important;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.10);
  padding: 8px 12px !important;
  min-height: 36px;
  height: auto;
  font-size: 13px !important;
  white-space: nowrap;
  display: inline-flex !important;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto; /* no shrink, enable horizontal scroll */
}
.chip i {
  font-size: 16px;
  color: rgba(39,48,63,0.85);
}

/* Thread */
.thread {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.msg {
  width: 100%;
  display: flex;
}
.msg.assistant {
  justify-content: flex-start;
}
.msg.user {
  justify-content: flex-end;
}
.bubble {
  max-width: 85%;
  padding: 10px 12px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 20px;
  box-sizing: border-box;
}
.msg.assistant .bubble {
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(39, 48, 63, 0.08);
  box-shadow: 0 6px 18px rgba(16, 24, 40, 0.06);
}
.msg.user .bubble {
  background: rgba(239, 126, 24, 0.12);
  border: 1px solid rgba(239, 126, 24, 0.35);
  box-shadow: 0 8px 22px rgba(239, 126, 24, 0.15);
}

/* Typing dots */
.typing {
  display: inline-flex;
  gap: 5px;
  align-items: center;
}
.loading-text {
  font-size: 14px;
  color: rgba(39, 48, 63, 0.7);
  margin-right: 8px;
  font-style: italic;
}
.dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: rgba(39, 48, 63, 0.7);
  animation: blink 1.2s infinite ease-in-out;
}
.dot:nth-child(3) { animation-delay: 0.15s; }
.dot:nth-child(4) { animation-delay: 0.3s; }
@keyframes blink {
  0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-2px); }
}

/* Composer */
.composer {
  /* occupy full height panel comfortably */
  margin-top: auto;
  position: sticky;
  bottom: 0;
  padding: 8px 12px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.65) 20%, rgba(255,255,255,0.85) 100%);
  -webkit-backdrop-filter: saturate(150%) blur(8px);
  backdrop-filter: saturate(150%) blur(8px);
}
.composer-inner {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: center;
  background: rgba(255, 255, 255, 0.75);
  -webkit-backdrop-filter: saturate(150%) blur(10px);
  backdrop-filter: saturate(150%) blur(10px);
  border: 1px solid rgba(39, 48, 63, 0.12);
  border-radius: 999px;
  overflow: hidden; /* clip textarea content to rounded corners */
  padding: 6px 6px 6px 12px;
  min-height: 36px;
  height: auto;
  box-shadow: 0 4px 12px rgba(16, 24, 40, 0.12);
}
/* tools removed per design */

/* Close button glass style - refined */
.close-btn {
  width: 34px !important;
  height: 34px !important;
  border-radius: 999px !important;
  background: rgba(255, 255, 255, 0.82) !important;
  -webkit-backdrop-filter: saturate(140%) blur(10px);
  backdrop-filter: saturate(140%) blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.6) !important;
  box-shadow: 0 4px 14px rgba(16,24,40,0.14);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform .15s ease, box-shadow .25s ease, background-color .2s ease;
}
.close-btn i {
  font-size: 20px;
  color: rgba(39,48,63,0.92);
  transition: transform .2s ease, opacity .2s ease;
}
.close-btn:hover {
  background: rgba(255, 255, 255, 0.92) !important;
  box-shadow: 0 8px 22px rgba(16,24,40,0.18);
}
.close-btn:hover i {
  transform: rotate(90deg);
}
.close-btn:active {
  transform: scale(.96);
}
.close-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(239,126,24,0.25), 0 8px 22px rgba(16,24,40,0.18);
}
.composer-inner textarea {
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  line-height: 20px;
  color: #27303f;
  resize: none;
  overflow-y: auto;        /* allow vertical scroll beyond clamp */
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch; /* smooth iOS scrolling */
  padding: 0 10px;  /* small horizontal padding to avoid touching corners */
  min-height: 22px;   /* start as 1 row */
  height: 22px;       /* explicit initial height for 1 row */
  max-height: 64px;   /* clamp to ~3 rows; can scroll inside when exceeded */
  width: 100%;
  word-break: break-word;
  overflow-wrap: anywhere;
}
.composer-inner textarea::placeholder {
  font-size: 14px !important;
  line-height: 20px !important;
}
.send {
  width: 36px !important;
  height: 36px !important;
  border-radius: 999px !important;
  background: var(--primary) !important;
  color: #fff !important;
  box-shadow: 0 10px 22px rgba(239, 126, 24, 0.35);
}
.send i { font-size: 18px; color: #fff; }
.send[disabled] {
  background: rgba(39, 48, 63, 0.2) !important;
  box-shadow: none;
}
.disclaimer {
  font-size: 11px;
  color: rgba(39, 48, 63, 0.55);
  text-align: center;
}

/* POI Components */
.pois-container,
.trip-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0;
  max-width: 85%;
}

.msg.assistant .poi-mini-cta,
.msg.assistant .trip-mini-cta {
  margin: 0; /* Remove individual margins since container handles spacing */
}

/* Choices */
.choices-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0;
  max-width: 85%;
}

.choice-btn {
  border-radius: 20px !important;
  background: rgba(239, 126, 24, 0.1) !important;
  border: 1px solid rgba(239, 126, 24, 0.3) !important;
  color: rgba(239, 126, 24, 0.9) !important;
  padding: 6px 12px !important;
  font-size: 13px !important;
  min-height: 32px !important;
  height: auto !important;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.choice-btn:hover {
  background: rgba(239, 126, 24, 0.2) !important;
  border-color: rgba(239, 126, 24, 0.5) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 126, 24, 0.2);
}

.choice-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(239, 126, 24, 0.15);
}

/* Mobile tweaks */
@media (max-width: 600px) {
  .ai-content { padding: 8px 14px 12px 14px; }
  .bubble { max-width: 90%; }
  .choices-container { max-width: 95%; }
}
/* When POI is open above, dim/freeze AI to avoid double scroll and let POI stand out */
.ai-assistant.peek {
  transform: translateY(-6px) scale(0.98);
  filter: saturate(95%) blur(1px);
  opacity: 0.98;
  pointer-events: none; /* prevent accidental scroll/taps on AI while POI is on top */
  transition: transform .18s ease, opacity .18s ease, filter .18s ease;
}
/* Hide composer while peeked to reduce clutter behind POI header */
.ai-assistant.peek .composer { 
  opacity: 0; 
  height: 0; 
  padding: 0; 
  margin: 0; 
  overflow: hidden; 
}
/* Soft scrim over the AI while POI is open to increase depth contrast */
.ai-assistant.peek::after {
  content: '';
  position: fixed;
  inset: 0;
  background: linear-gradient(180deg, rgba(255,255,255,0.80) 0%, rgba(255,255,255,0.65) 35%, rgba(255,255,255,0.50) 100%);
  pointer-events: none;
  z-index: 2; /* above AI content, below the slideout POI which has higher z */
}

</style>
